import { AssistChip, Box, ChipGroup } from '@zillow/constellation-10';
import { IconMessageFilled, IconProfileFilled } from '@zillow/constellation-icons';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useConversationMessagesQuery } from '../../hooks/useConversationMessagesQuery';
import type { ConversationMessagesData } from '../../utils/conversationMessageUtils';
import { ModalType } from '../../types/ModalType';
import {
  emitDisplayRequestRenterProfileButtonEvent,
  emitRequestRenterProfileClickEvent,
  REQUEST_RENTER_PROFILE_CLICK_EVENT_TYPE_ID,
} from '../../constants/clickstreamEvents/requestRenterProfile';
import type { TriggerLocation } from '../../constants/clickstreamEvents/globals';
import {
  CHOOSE_SAVED_REPLY_CLICK_EVENT_TYPE_ID,
  emitChooseSavedReplyClickEvent,
} from '../../constants/clickstreamEvents/savedReplyModal';
import StringConstants from '../../constants/StringConstants';
import useModal from '../../hooks/useModal';
import { AppContext } from '../../contexts/AppContextProvider';
import RequestRenterProfileModal from '../Modals/RequestRenterProfileModal';
import SavedReplyModal from '../Modals/SavedReplyModal';
import { TooltipWrapper } from '@zg-rentals/satellite-components';
import useConversationActions from '../../hooks/useConversationActions';
import { useAuth } from '../../AuthProvider';
import { compactContainerStyles, defaultContainerStyles } from './styles';

export type MessageListActionsData = {
  isPhoneLead: boolean;
  isConversationBlockedByZillow: boolean;
  isSpam: boolean;
  renterName: string;
  shouldShowRequestRenterProfileCta: boolean;
};

export const selectMessageListActionsData = (data: ConversationMessagesData): MessageListActionsData => ({
  isPhoneLead: data.isPhoneLead,
  isConversationBlockedByZillow: data.isConversationBlockedByZillow,
  isSpam: data.status.isSpam,
  renterName: data.renterName ?? '',
  shouldShowRequestRenterProfileCta: data.renterProfile.shouldShowRequestRenterProfileCta,
});

type MessageListActionsProps = {
  conversationId: string;
  listingAlias: string;
  leadId?: string | null;
  clientTimeZone: string;
  variant?: 'default' | 'compact';
  triggerLocation: TriggerLocation;
};

export default function MessageListActions({
  conversationId,
  listingAlias,
  leadId,
  clientTimeZone,
  variant = 'default',
  triggerLocation,
}: MessageListActionsProps): JSX.Element {
  const isCompact = variant === 'compact';
  const { isImpersonator } = useContext(AppContext);
  const { activeModal, setActiveModal } = useModal();
  const { user } = useAuth();
  const { hasErrorOnRequestRenterProfile, requestRenterProfile } = useConversationActions();
  const [isRequestProfileModalOpen, setIsRequestProfileModalOpen] = useState(false);

  // Cache-only read — `MessageListItems` owns the `getMessages` fetch + polling.
  const { data: conversationData } = useConversationMessagesQuery({
    conversationId,
    clientTimeZone,
    enabled: false,
    select: selectMessageListActionsData,
  });

  const { isPhoneLead, isConversationBlockedByZillow, isSpam, renterName, shouldShowRequestRenterProfileCta } =
    conversationData || {};
  const isChooseSavedReplyDisabled = Boolean(isImpersonator || isPhoneLead || isSpam || isConversationBlockedByZillow);

  useEffect(() => {
    if (shouldShowRequestRenterProfileCta) {
      emitDisplayRequestRenterProfileButtonEvent({
        user,
        listing: { listingAlias },
        triggerLocation,
        lead: { leadId },
      });
    }
  }, [shouldShowRequestRenterProfileCta, user, listingAlias, leadId, triggerLocation]);

  const chooseSavedReplyTooltipText = useMemo(() => {
    if (isChooseSavedReplyDisabled) {
      if (isPhoneLead) {
        return StringConstants.TOOLTIP.PHONE_LEAD;
      }
      if (isImpersonator) {
        return StringConstants.TOOLTIP.IMPERSONATION;
      }
      if (isSpam) {
        return StringConstants.TOOLTIP.REPORTED;
      }
      if (isConversationBlockedByZillow) {
        return StringConstants.TOOLTIP.BLOCKED_BY_ZILLOW;
      }
    }

    return '';
  }, [isChooseSavedReplyDisabled, isImpersonator, isPhoneLead, isSpam, isConversationBlockedByZillow]);

  const handleShowSavedReplyModal = (): void => {
    setActiveModal(ModalType.SavedReplyModal);
    emitChooseSavedReplyClickEvent({ user, listing: { listingAlias }, triggerLocation, lead: { leadId } });
  };

  const handleShowRequestRenterProfileModal = (): void => {
    // Local state, not the shared `activeModal` slot: routing this modal through that slot double-renders
    // it (messaging modal + page-level LeadManagementModals) and tears down the renter details drawer.
    setIsRequestProfileModalOpen(true);
    emitRequestRenterProfileClickEvent({ user, listing: { listingAlias }, triggerLocation, lead: { leadId } });
  };

  return (
    <>
      {activeModal === ModalType.SavedReplyModal && (
        <SavedReplyModal listingAlias={listingAlias} leadId={leadId} triggerLocation={triggerLocation} />
      )}

      {isRequestProfileModalOpen && (
        <RequestRenterProfileModal
          name={renterName || ''}
          onRequestRenterProfileClick={() =>
            requestRenterProfile({
              conversationId,
              listingAlias,
              clientTimeZone,
              onSuccess: () => setIsRequestProfileModalOpen(false),
            })
          }
          hasError={hasErrorOnRequestRenterProfile}
          onClose={() => setIsRequestProfileModalOpen(false)}
        />
      )}

      <Box css={isCompact ? compactContainerStyles : defaultContainerStyles}>
        <ChipGroup
          aria-label="Message actions"
          css={{
            flexWrap: 'nowrap',
            '& > *': { flexShrink: 0 },
          }}
        >
          <TooltipWrapper
            tooltipPlacement="top"
            wrapInTooltip={isChooseSavedReplyDisabled}
            tooltipText={chooseSavedReplyTooltipText}
          >
            <AssistChip
              data-eventid={CHOOSE_SAVED_REPLY_CLICK_EVENT_TYPE_ID}
              aria-haspopup="true"
              aria-label="View saved reply messages"
              disabled={isChooseSavedReplyDisabled}
              onClick={handleShowSavedReplyModal}
              icon={<IconMessageFilled />}
            >
              Choose saved reply
            </AssistChip>
          </TooltipWrapper>

          {shouldShowRequestRenterProfileCta && (
            <TooltipWrapper
              tooltipPlacement="top"
              wrapInTooltip={isImpersonator}
              tooltipText={StringConstants.TOOLTIP.IMPERSONATION}
            >
              <AssistChip
                data-eventid={REQUEST_RENTER_PROFILE_CLICK_EVENT_TYPE_ID}
                aria-haspopup="true"
                aria-label="Request more information"
                disabled={isImpersonator}
                onClick={handleShowRequestRenterProfileModal}
                icon={<IconProfileFilled />}
              >
                Request more information
              </AssistChip>
            </TooltipWrapper>
          )}
        </ChipGroup>
      </Box>
    </>
  );
}
